import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { AddManualLotEnabled } from 'common/utils';
import titleCase from 'common/utils/titleCase';
import { isValidLotNumber } from 'modules/StockManagement/utils/stockLineValidation';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';

import SYSTEM_DEFICIT from 'common/constants/inventoryLabels';
import useManualLotEntry, { budOf, normalizeLot } from '../hooks/useManualLotEntry';
import ndcsMatch from '../utils/ndcsMatch';
import ManualLotPanel from './ManualLotPanel';

const ndcOptionLabel = (option) => {
  const parts = [option.ndc, option.isDefault ? '★' : null, option.manufacturer || null].filter(
    Boolean
  );
  return option.isDefault ? parts.join(' ') : parts.join(' · ');
};

/** Manual fallback for vials too large for the scanner rig, and — with manual
 *  lot entry enabled (POM-6083 parity) — for vials whose lot inventory does
 *  not hold in stock.
 *
 *  NDC first: the operator picks the NDC printed on the vial from the product's
 *  closed list (defaulting to the rejected read's NDC, else the Rx's own). The
 *  lot field then offers that NDC's lots AT THIS PHARMACY (`facility`, see
 *  useFacilityLots): its dispensable stock, then its depleted lots. Picking or
 *  typing a stocked lot adds an ordinary read-only vial row, exactly as
 *  before. Picking a depleted lot, or typing a lot number neither list knows —
 *  including one stocked only at ANOTHER pharmacy — records the vial BY HAND as
 *  an unverified lot: the re-key confirmation (new lot only) and the BUD read
 *  off the vial are collected, and the fulfillment resolves-or-creates the row
 *  here and posts the quantity to unverified_qty. See useManualLotEntry.
 *
 *  Until the facility's stock has loaded (or if that fetch failed) the stock
 *  options fall back to the scan-app listing, which has no pharmacy dimension —
 *  a lot picked from it can still be refused at commit if it is elsewhere.
 *
 *  With manual entry off there is no NDC field; the lot is matched against the
 *  product's in-stock lots only and anything else is refused, as before.
 *
 *  `seed` pre-fills the dialog from a vial read the scanner REJECTED (see
 *  wireScanEvent's onUnverifiedVial): lot number and NDC land in the fields so
 *  the operator confirms rather than re-types them.
 *
 *  The Rx's prescribed product id must be resolvable (see
 *  resolvePrescribedProductId) for this dialog to do anything at all — every
 *  list here is that product's, so an operator can never add a vial for a
 *  different product just because its lot number happens to collide with one
 *  in inventory. */
const AddDrugByLotDialog = ({
  open,
  onClose,
  inventory,
  productId,
  rxNdc,
  fulfillingPharmacyId,
  facility,
  seed,
  onConfirm,
  onConfirmManual,
}) => {
  const [lotNumber, setLotNumber] = useState('');
  const [notFound, setNotFound] = useState(false);

  const typedLot = lotNumber.trim();
  const manualAvailable = AddManualLotEnabled && !!productId && !!fulfillingPharmacyId;

  // Refresh this pharmacy's lots on every open, so a receipt landed since the session began
  // (or since the last open) is offered rather than typed by hand.
  const reloadFacility = facility?.reload;
  useEffect(() => {
    if (open && reloadFacility) reloadFacility();
  }, [open, reloadFacility]);

  // Lot numbers inventory reports as depleted at this pharmacy for the product. A listing lot
  // in this set is NOT in stock, whatever the listing implies.
  const depletedLotNumbers = useMemo(
    () => new Set((facility?.depletedLots ?? []).map((lot) => normalizeLot(lot.lotNumber))),
    [facility]
  );

  // The product's lots from the scan-app listing, one row per lot number, all NDCs. Carries the
  // strength the facility lists lack — and is the fallback stock source when they have not
  // loaded.
  const listingLots = useMemo(() => {
    if (!productId) return [];
    const byLot = new Map();
    inventory
      .filter((row) => row.productId === productId && normalizeLot(row.lotNumber))
      .forEach((row) => {
        if (!byLot.has(normalizeLot(row.lotNumber))) byLot.set(normalizeLot(row.lotNumber), row);
      });
    return [...byLot.values()];
  }, [inventory, productId]);

  // Strength is not on the facility rows; the scan-app listing knows it for the product.
  const productStrength = useMemo(
    () => inventory.find((row) => row.productId === productId && row.strength)?.strength ?? '',
    [inventory, productId]
  );

  // The product's dispensable stock AT THIS PHARMACY, in the vial-row shape addVialFromInventory
  // expects (drugName / manufacturer / strength / ndc / lotNumber / productId), plus the real
  // inventoryLotId so a commit can name the row. Falls back to the listing until loaded.
  const productLots = useMemo(() => {
    if (!facility?.availableLoaded) return listingLots;
    const strengthFor = (lot) =>
      listingLots.find((row) => normalizeLot(row.lotNumber) === normalizeLot(lot.lotNumber))
        ?.strength ?? productStrength;
    return facility.availableLots.map((lot) => ({
      drugName: lot.drugName ?? '',
      manufacturer: lot.manufacturer ?? '',
      strength: strengthFor(lot),
      ndc: lot.ndc,
      lotNumber: lot.lotNumber,
      productId,
      inventoryLotId: lot.inventoryLotId,
    }));
  }, [facility, listingLots, productStrength, productId]);

  // Resolved in two steps because the hook needs to know whether the typed lot matched the
  // listing, and the listing match depends on the NDC the hook owns.
  const [selectedMatch, setSelectedMatch] = useState(null);
  const manual = useManualLotEntry({
    open,
    available: manualAvailable,
    productId,
    fulfillingPharmacyId,
    facility,
    rxNdc,
    seedNdc: seed?.ndc ?? null,
    typedLot,
    hasListingMatch: !!selectedMatch,
    productLots,
  });

  // In-stock lots offered for matching: this pharmacy's stock (or the listing, until it loads,
  // minus anything inventory reports as depleted here). Scoped to the selected NDC when there
  // is one — a lot number is only unique within an NDC, and adding a vial under the wrong NDC
  // is the mistake the NDC field exists to prevent.
  // The depleted filter is for the listing only: it is keyed by lot number, and a lot number
  // repeats across shipments, so applied to this pharmacy's own stock it would hide a stocked
  // row just because an older row under the same lot number has drained.
  const stockLots = useMemo(
    () =>
      productLots.filter(
        (row) =>
          (facility?.availableLoaded || !depletedLotNumbers.has(normalizeLot(row.lotNumber))) &&
          (!manualAvailable ||
            !manual.manualNdc ||
            (row.ndc && ndcsMatch(row.ndc, manual.manualNdc)))
      ),
    [productLots, facility, manualAvailable, manual.manualNdc, depletedLotNumbers]
  );

  // Matched only against this Rx's product lots, never the full inventory — a lot number that
  // happens to match a different product must never be addable here. Re-derived whenever the
  // NDC changes, since the candidate set changes with it.
  const findStockMatch = (value) =>
    value
      ? (stockLots.find((row) => normalizeLot(row.lotNumber) === normalizeLot(value)) ?? null)
      : null;
  useEffect(() => {
    setSelectedMatch(findStockMatch(typedLot));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stockLots, typedLot]);

  // Seed once per open / per new seed — never on ordinary re-renders, so a correction typed
  // over the misread is kept.
  useEffect(() => {
    if (open && seed?.lotNumber) setLotNumber(String(seed.lotNumber));
  }, [open, seed]);

  // The lot field's options: the selected NDC's in-stock lots, then its depleted lots. A depleted
  // lot whose number is also in stock is left out — the lot number resolves to the stocked row
  // anyway, so the "No stock" twin only reads as a contradiction.
  const options = useMemo(() => {
    const stockedLotNumbers = new Set(stockLots.map((row) => normalizeLot(row.lotNumber)));
    return [
      ...stockLots.map((row) => ({ ...row, kind: 'inventory' })),
      ...(manualAvailable ? manual.depletedLotChoices : [])
        .filter((lot) => !stockedLotNumbers.has(normalizeLot(lot.lotNumber)))
        .map((lot) => ({ ...lot, kind: 'depleted' })),
    ];
  }, [stockLots, manual.depletedLotChoices, manualAvailable]);

  // freeSolo suppresses MUI's "No options" popper entirely (by design, so it doesn't get in the
  // way of free text) — without this the field can look broken any time there's nothing to
  // suggest, rather than explaining why.
  let suggestionHint;
  if (!productId) {
    suggestionHint =
      "This Rx's product isn't recognized in the current inventory listing — manual add is unavailable.";
  } else if (manual.active) {
    suggestionHint = manual.existingLot
      ? `Not in stock — matches an existing inventory lot with no stock. It will be recorded as a ${SYSTEM_DEFICIT.toLowerCase()} lot.`
      : `Not in stock — it will be recorded by hand as a new ${SYSTEM_DEFICIT.toLowerCase()} lot.`;
  } else if (manualAvailable) {
    suggestionHint =
      options.length === 0
        ? 'No lots at this pharmacy for this NDC yet — type the lot number printed on the vial.'
        : 'Pick a lot at this pharmacy, or type the lot number printed on the vial.';
  } else if (options.length === 0) {
    suggestionHint = 'No inventory lots found for this product yet — type the lot number directly.';
  }

  const handleClose = () => {
    setLotNumber('');
    setNotFound(false);
    setSelectedMatch(null);
    manual.reset();
    onClose();
  };

  const handleInputChange = (_, newInputValue) => {
    setLotNumber(newInputValue);
    if (notFound) setNotFound(false);
    manual.onLotNumberChanged();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!productId || !typedLot) return;
    if (selectedMatch) {
      onConfirm(selectedMatch);
      handleClose();
      return;
    }
    if (!manual.active) {
      setNotFound(true);
      return;
    }
    if (manual.error || manual.lookupPending) return;
    onConfirmManual(manual.buildVialFields({ productStrength }));
    handleClose();
  };

  const submitDisabled =
    !productId || !typedLot || (manual.active && (!!manual.error || manual.lookupPending));

  return (
    <Dialog open={open} onClose={handleClose} aria-labelledby='add-drug-by-lot-title' fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle id='add-drug-by-lot-title'>Add drug by lot number</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {manualAvailable
              ? `Select the NDC printed on the vial, then pick its lot or type the lot number. A lot inventory does not hold in stock is recorded by hand as a ${SYSTEM_DEFICIT.toLowerCase()} lot.`
              : 'Please enter the lot number and it will be matched against the current inventory listing.'}
          </DialogContentText>

          {manualAvailable && (
            <>
              {manual.recordableNotice && (
                <Alert severity='info' sx={{ mb: 1.5, py: 0.25 }}>
                  {manual.recordableNotice}
                </Alert>
              )}
              {manual.recordableLoading && (
                <Alert severity='info' sx={{ mb: 1.5, py: 0.25 }}>
                  Loading this product&apos;s NDCs and lots from inventory…
                </Alert>
              )}
              {/* ALWAYS a closed list, never typeable — a claim against an NDC inventory has no
                  master row for cannot be reconciled downstream, and core rejects it outright. */}
              <TextField
                select
                fullWidth
                size='small'
                label='NDC'
                required
                value={manual.manualNdc}
                onChange={(e) => manual.onNdcChange(e.target.value)}
                disabled={manual.recordableLoading || !manual.hasNdcChoices}
                helperText={
                  manual.recordableLoading
                    ? 'Loading the NDCs for this product…'
                    : 'The NDC printed on the vial. ★ is the manufacturer default.'
                }
                inputProps={{ 'data-testid': 'manual-lot-ndc' }}
                sx={{ mb: 2 }}
              >
                {manual.ndcOptions.map((option) => (
                  <MenuItem key={option.ndc} value={option.ndc}>
                    {ndcOptionLabel(option)}
                  </MenuItem>
                ))}
              </TextField>
            </>
          )}

          <Autocomplete
            autoFocus
            fullWidth
            freeSolo
            size='small'
            disabled={!productId || (manualAvailable && manual.recordableLoading)}
            options={options}
            getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.lotNumber)}
            inputValue={lotNumber}
            onInputChange={handleInputChange}
            renderOption={(props, opt) => (
              // eslint-disable-next-line react/jsx-props-no-spreading
              <li {...props} key={`${opt.kind}-${opt.inventoryLotId ?? opt.lotNumber}`}>
                <Box>
                  <Typography variant='body2' sx={{ fontWeight: 600 }}>
                    {opt.lotNumber}
                  </Typography>
                  {opt.kind === 'inventory' ? (
                    <Typography variant='body2' color='text.secondary'>
                      {titleCase(opt.drugName)}
                      {opt.manufacturer ? ` — ${opt.manufacturer}` : ''}
                      {opt.ndc ? ` — NDC ${opt.ndc}` : ''}
                    </Typography>
                  ) : (
                    <Typography variant='body2' color='text.secondary'>
                      No stock — {SYSTEM_DEFICIT.toLowerCase()} lot
                      {opt.expirationDate ? ` · BUD ${budOf(opt)}` : ' · no BUD recorded'}
                      {opt.isExpired ? ' · expired' : ''}
                      {parseFloat(opt.unverifiedQty) > 0
                        ? ` · ${opt.unverifiedQty} already in ${SYSTEM_DEFICIT.toLowerCase()}`
                        : ''}
                    </Typography>
                  )}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...params}
                label='Lot Number'
                error={notFound || (manual.active && !isValidLotNumber(typedLot))}
                helperText={notFound ? 'No inventory match for this lot number.' : suggestionHint}
              />
            )}
          />

          {selectedMatch && (
            <Box
              sx={{ mt: 2, p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
            >
              <Stack spacing={0.5}>
                <Typography variant='body2' sx={{ fontWeight: 600 }}>
                  {titleCase(selectedMatch.drugName)}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  NDC {selectedMatch.ndc}
                  {selectedMatch.manufacturer ? ` — ${selectedMatch.manufacturer}` : ''}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Lot {selectedMatch.lotNumber}
                </Typography>
              </Stack>
            </Box>
          )}

          {manual.active && <ManualLotPanel typedLot={typedLot} entry={manual} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color='inherit'>
            Cancel
          </Button>
          <Button type='submit' variant='contained' disabled={submitDisabled}>
            {manual.active ? `Add ${SYSTEM_DEFICIT.toLowerCase()} lot` : 'Add'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

AddDrugByLotDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  inventory: PropTypes.arrayOf(
    PropTypes.shape({
      drugName: PropTypes.string,
      manufacturer: PropTypes.string,
      strength: PropTypes.string,
      ndc: PropTypes.string,
      lotNumber: PropTypes.string,
      productId: PropTypes.string,
    })
  ),
  productId: PropTypes.string,
  rxNdc: PropTypes.string,
  fulfillingPharmacyId: PropTypes.string,
  facility: PropTypes.shape({
    ndcOptions: PropTypes.arrayOf(PropTypes.shape({ ndc: PropTypes.string })),
    depletedLots: PropTypes.arrayOf(PropTypes.shape({ lotNumber: PropTypes.string })),
    availableLots: PropTypes.arrayOf(PropTypes.shape({ lotNumber: PropTypes.string })),
    availableLoaded: PropTypes.bool,
    hasInventoryMaster: PropTypes.bool,
    loading: PropTypes.bool,
    notice: PropTypes.string,
    reload: PropTypes.func,
  }),
  seed: PropTypes.shape({ lotNumber: PropTypes.string, ndc: PropTypes.string }),
  onConfirm: PropTypes.func.isRequired,
  onConfirmManual: PropTypes.func.isRequired,
};

AddDrugByLotDialog.defaultProps = {
  inventory: [],
  productId: null,
  rxNdc: null,
  fulfillingPharmacyId: null,
  facility: null,
  seed: null,
};

export default AddDrugByLotDialog;
